// Multi-stem canvas waveform renderer, after Photon's visualizer
// (cpopsa/src/gui/waveform/featurelist.md):
//
// Demix scene — overlapping stems on one canvas 2D context:
//   - Each stem's shape comes from a Uint8Array of per-column max-abs peaks.
//   - Per-stem amplitude = peak × linear gain (dB slider), clamped to the band.
//   - Filled body at 55% alpha + 1.5-CSS-px opaque outline in the stem colour.
//   - Symmetric around a horizontal midline (bandCenter ± amp).
//   - Fixed z-order: iterated in layer order; last drawn is frontmost.
//   - Muted / not-soloed stems are drawn grey (the app uses 0.4 grey, not alpha).
//   - 0.25-alpha dark baseline at the band centre.
//
// Restore scene — Photon's VoiceEnhance display (VoiceEnhance_Waveform_Color_Spec):
//   - Input bar (before peaks) light grey-blue, opaque, full height, underneath.
//   - Processed bar on top: amplitude lerp(before, after, strength), dark green,
//     body alpha 0.85·strength, outline alpha 1.0·strength. Bypass: input only.
//
// Redraws only when state changes (slider input, mute/solo, peaks load, resize).
// Playhead is a separate absolutely-positioned DOM element owned by the host widget.

import type { Hex } from '@/state/models';
import { parseHex, type Rgb } from './color';

export interface WaveformEvents {
  seek: (timeSeconds: number) => void;
}

export type PeakData = Record<string, Uint8Array>;

export interface DemixLayer {
  name: string;    // peaks key
  color: Hex;
  gain: number;    // linear
  grey: boolean;   // muted, or another stem is soloed
}

export type WaveScene =
  | { kind: 'demix'; layers: DemixLayer[] }   // Z order: index 0 is backmost
  // `bypass` draws the input bar alone; the host keeps it false so the widget's
  // Bypass never changes the picture (the app's VE display supports it).
  | { kind: 'restore'; strength: number; bypass: boolean };

const BODY_ALPHA = 0.55;
const OUTLINE_PX = 1.5;   // kOutlinePixels
const GREY: Rgb = [0x66, 0x66, 0x66];           // 0.4 grey
const INPUT_RGB: Rgb = [0xa6, 0xb3, 0xbf];      // (0.65, 0.70, 0.75)
const PROCESSED_RGB: Rgb = [0x33, 0x61, 0x38];  // (0.20, 0.38, 0.22)
const PROCESSED_BODY_ALPHA = 0.85;
const BASELINE_STYLE = 'rgba(0, 0, 0, 0.25)';

export class WaveformRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private events: WaveformEvents;

  private widthCss = 0;
  private heightCss = 0;
  private dpr = 1;

  private durationS = 0;
  private peaks: PeakData | null = null;
  private scene: WaveScene | null = null;

  private resizeObserver: ResizeObserver | null = null;
  private rafPending = false;

  // Scrub: pointer captured on the canvas, seeks coalesced to one per frame
  // because every seek restarts the engine's sources.
  private scrubPointerId: number | null = null;
  private scrubPendingTime: number | null = null;
  private scrubRaf: number | null = null;

  constructor(canvas: HTMLCanvasElement, events: WaveformEvents) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to acquire 2D canvas context');
    this.ctx = ctx;
    this.events = events;

    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    canvas.addEventListener('keydown', this.onKeyDown);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas);

    this.handleResize();
  }

  setDuration(seconds: number): void {
    this.durationS = seconds;
  }

  setPeaks(peaks: PeakData): void {
    this.peaks = peaks;
    this.scheduleRender();
  }

  setScene(scene: WaveScene): void {
    this.scene = scene;
    this.scheduleRender();
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('keydown', this.onKeyDown);
    this.resizeObserver?.disconnect();
    if (this.scrubRaf !== null) cancelAnimationFrame(this.scrubRaf);
  }

  private scheduleRender(): void {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.render();
    });
  }

  private handleResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const newW = Math.max(1, Math.round(rect.width));
    const newH = Math.max(1, Math.round(rect.height));
    const newDpr = Math.min(window.devicePixelRatio || 1, 2);

    // Skip if nothing meaningful changed — avoids clearing the canvas on
    // ResizeObserver notifications that don't actually resize us.
    if (newW === this.widthCss && newH === this.heightCss && newDpr === this.dpr) {
      return;
    }

    this.widthCss = newW;
    this.heightCss = newH;
    this.dpr = newDpr;
    this.canvas.width = this.widthCss * this.dpr;
    this.canvas.height = this.heightCss * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    // Render synchronously within the same tick as the canvas clear. A
    // scheduled RAF render here would leave a blank frame between resize and
    // draw, producing a visible flash during a resize drag.
    this.render();
  }

  private render(): void {
    const { ctx, widthCss: w, heightCss: h } = this;
    ctx.clearRect(0, 0, w, h);
    if (!this.peaks || !this.scene) return;

    if (this.scene.kind === 'demix') {
      for (const layer of this.scene.layers) {
        const peakBytes = this.peaks[layer.name];
        if (!peakBytes) continue;
        const rgb = layer.grey ? GREY : parseHex(layer.color) ?? GREY;
        this.drawBand(peakBytes, null, 0, layer.gain, rgb, BODY_ALPHA, 1.0);
      }
    } else {
      const before = this.peaks.before;
      const after = this.peaks.after;
      if (!before) return;
      this.drawBand(before, null, 0, 1, INPUT_RGB, 1.0, 1.0);
      if (!this.scene.bypass && after) {
        const s = this.scene.strength;
        if (s > 0) {
          this.drawBand(before, after, s, 1, PROCESSED_RGB, PROCESSED_BODY_ALPHA * s, s);
        }
      }
    }

    // Baseline at the band centre, over the bodies (the app blends it in).
    ctx.fillStyle = BASELINE_STYLE;
    ctx.fillRect(0, Math.round(h / 2) - 0.5, w, 1);
  }

  // One symmetric band. When `mixWith` is given the per-column peak is
  // lerp(peaks, mixWith, mix) (the restore scene's processed bar).
  private drawBand(
    peaks: Uint8Array,
    mixWith: Uint8Array | null,
    mix: number,
    gain: number,
    rgb: Rgb,
    bodyAlpha: number,
    outlineAlpha: number,
  ): void {
    const { ctx, widthCss: w, heightCss: h } = this;
    const midline = h / 2;
    const halfHeight = h / 2;

    // One visual column per 2 CSS pixels, snapped to 1+ columns.
    const drawnColumns = Math.max(1, Math.round(w / 2));
    const colWidth = w / drawnColumns;
    const srcColumns = peaks.length;

    const bodyStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${bodyAlpha})`;
    const outlineStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${outlineAlpha})`;

    for (let c = 0; c < drawnColumns; c++) {
      const srcIdxStart = Math.floor((c * srcColumns) / drawnColumns);
      const srcIdxEnd = Math.max(
        srcIdxStart + 1,
        Math.floor(((c + 1) * srcColumns) / drawnColumns),
      );
      // Max-pool over the source range so coarser draw widths still use the
      // loudest peak in each pool.
      let peak = 0;
      let peak2 = 0;
      for (let k = srcIdxStart; k < srcIdxEnd && k < srcColumns; k++) {
        if (peaks[k] > peak) peak = peaks[k];
        if (mixWith && mixWith[k] > peak2) peak2 = mixWith[k];
      }
      let peakNorm = peak / 255;
      if (mixWith) peakNorm = peakNorm + (peak2 / 255 - peakNorm) * mix;
      const amp = Math.min(halfHeight, peakNorm * gain * halfHeight);
      if (amp < 0.5) continue; // below visual threshold

      const x = c * colWidth;
      ctx.fillStyle = bodyStyle;
      ctx.fillRect(x, midline - amp, colWidth, amp * 2);
      ctx.fillStyle = outlineStyle;
      const edge = Math.min(OUTLINE_PX, amp);
      ctx.fillRect(x, midline - amp, colWidth, edge);
      ctx.fillRect(x, midline + amp - edge, colWidth, edge);
    }
  }

  private timeAt(clientX: number): number {
    const rect = this.canvas.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return frac * this.durationS;
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (this.durationS <= 0 || e.button !== 0) return;
    this.scrubPointerId = e.pointerId;
    try { this.canvas.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    this.events.seek(this.timeAt(e.clientX));
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (this.scrubPointerId !== e.pointerId) return;
    this.scrubPendingTime = this.timeAt(e.clientX);
    if (this.scrubRaf !== null) return;
    this.scrubRaf = requestAnimationFrame(() => {
      this.scrubRaf = null;
      if (this.scrubPendingTime !== null) {
        this.events.seek(this.scrubPendingTime);
        this.scrubPendingTime = null;
      }
    });
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (this.scrubPointerId !== e.pointerId) return;
    this.scrubPointerId = null;
    try { this.canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (this.durationS <= 0) return;
    const STEP_S = 1;
    const ariaNow = Number(this.canvas.getAttribute('aria-valuenow') || 0);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.events.seek(Math.max(0, ariaNow - STEP_S));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.events.seek(Math.min(this.durationS, ariaNow + STEP_S));
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.events.seek(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.events.seek(this.durationS);
    }
  };
}
